import { DeleteOutlined } from '@ant-design/icons'
import { useMutation } from '@apollo/client'
import { Button, Checkbox, Col, Form, Input, InputNumber, message, Popconfirm, Row, Space, Spin } from 'antd'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useLoginModal } from '../../../../context/loginModalContext'
import { useUser } from '../../../../context/userContext'
import { showAuthError } from '../../../../shared/utils/webHelpers'
import { StyledDrawer } from '../../../common/CommonStyles'
import { useForceUpdate } from '../../../../shared/hooks/useForceUpdate'
import { v4 as uuidv4 } from 'uuid'
import CreateNewItemExtraButtons from '../../../common/CreateNewItemExtraButtons'
import EDIT_POLICY_CLASS from '@dentolize/clinic-mobile/src/shared/store/mutations/actions/policyClasses/editPolicyClass'
import DELETE_POLICY_CLASS from '@dentolize/clinic-mobile/src/shared/store/mutations/actions/policyClasses/deletePolicyClass'
import ADD_NEW_POLICY_CLASS from '@dentolize/clinic-mobile/src/shared/store/mutations/actions/policyClasses/addNewPolicyClass'
import AmountPriceField from '../../../common/formFields/AmountPriceField'
import { toEnglishNumbers } from '../../../../shared/utils/helpers'
import SearchField from '../../../common/searchFields/SearchField'
import SEARCH_INSURANCE_POLICIES from '@dentolize/clinic-mobile/src/shared/store/queries/actions/insurancePolicies/searchInsurancePolicies'

const PolicyClassForm = props => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const { user } = useUser()
  const { insurancePolicy, insuranceCompany } = useParams()
  const { setLoginModalVisible } = useLoginModal()
  const [forceUpdate, handleForceUpdate] = useForceUpdate()

  const { formVisible, selectedItem, handleClose } = props
  const { validateFields, resetFields, setFieldsValue } = form

  const generatedID = useMemo(uuidv4, [formVisible, forceUpdate])

  useEffect(() => {
    resetFields()
  }, [formVisible])

  const [addPolicyClass, { loading }] = useMutation(ADD_NEW_POLICY_CLASS)
  const [editPolicyClass, { loading: loadingEdit }] = useMutation(EDIT_POLICY_CLASS)
  const [deletePolicyClass, { loading: loadingDelete }] = useMutation(DELETE_POLICY_CLASS, {
    update: handleUpdateAfterDelete
  })

  const insuranceUnlimited = Form.useWatch('insuranceUnlimited', form)
  const fullDiscount = Form.useWatch('fullDiscount', form)

  useEffect(() => {
    if (insuranceUnlimited) {
      setFieldsValue({ insuranceLimit: null })
    }
  }, [insuranceUnlimited])

  useEffect(() => {
    if (fullDiscount) {
      setFieldsValue({ insurancePercentage: 100 })
    }
  }, [fullDiscount])

  const handleSubmit = key => {
    validateFields()
      .then(async values => {
        try {
          const variables = {
            ...values,
            insurancePercentage: values.insurancePercentage,
            insuranceLimit: values.insuranceUnlimited ? null : values.insuranceLimit,
            maxLimit: values.maxLimit
          }

          let res
          if (!selectedItem || selectedItem?.new) {
            res = await addPolicyClass({
              variables: {
                ...variables,
                id: generatedID,
                insurancePolicy: insurancePolicy || selectedItem.insurancePolicy || values.insurancePolicy.key
              }
            })
            if (key === 'view') {
              navigate(`/associates/policy-class/${res.data.addNewPolicyClass.id}`)
              handleClose()
            } else if (key === 'new') {
              resetFields()
              handleForceUpdate()
            } else {
              handleClose(res.data.addNewPolicyClass)
            }
          } else {
            res = await editPolicyClass({
              variables: { policyClass: selectedItem.id, ...variables }
            })
            handleClose()
          }

          message.success(`${selectedItem.new ? t('common.createdSuccessfully') : t('common.updatedSuccessfully')}`)

          if (props.handleAnimateRow) {
            props.handleAnimateRow(
              (res.data.addNewPolicyClass && res.data.addNewPolicyClass.id) ||
                (res.data.editPolicyClass && res.data.editPolicyClass.id),
              selectedItem.new
            )
          }
        } catch (e) {
          showAuthError(e, t, setLoginModalVisible)
        }
      })
      .catch(() => {})
  }

  const handleDelete = async () => {
    try {
      await deletePolicyClass({
        variables: { policyClass: selectedItem.id }
      })
      message.success(t('common.deletedSuccess'))
      handleClose()
    } catch (e) {
      showAuthError(e, t, setLoginModalVisible)
    }
  }

  function handleUpdateAfterDelete(store, deletedItem) {
    store.data.delete(`PolicyClass:${deletedItem.data.deletePolicyClass.id}`)
    props.handleAnimateRow && props.handleAnimateRow(null, true)
  }

  const title = !selectedItem || selectedItem?.new ? t('patients.newItem') : t('patients.editItem')

  return (
    <StyledDrawer
      title={title}
      open={formVisible}
      onClose={handleClose}
      destroyOnClose
      footer={
        <div className="flex-container justify-between">
          {!selectedItem.new && user.permissions.deleteInsuranceCompany ? (
            <Popconfirm
              title={t('settings.insurance.sureDelete')}
              okText={t('common.yes')}
              cancelText={t('common.no')}
              onConfirm={handleDelete}
              disabled={loading || loadingEdit}
            >
              <Button loading={loadingDelete} disabled={loading || loadingEdit} type="primary" danger icon={<DeleteOutlined />}>
                {t('common.delete')}
              </Button>
            </Popconfirm>
          ) : selectedItem.new && selectedItem.fromDrawer ? (
            <CreateNewItemExtraButtons handleSubmit={handleSubmit} disabled={loading} />
          ) : (
            <span />
          )}
          <Button
            loading={loading || loadingEdit}
            disabled={loadingDelete}
            htmlType="submit"
            type="primary"
            form={`${generatedID}_PolicyClassForm`}
          >
            {selectedItem.new ? t('common.create') : t('common.update')}
          </Button>
        </div>
      }
    >
      <Spin spinning={loading || loadingEdit || loadingDelete}>
        <Form
          form={form}
          scrollToFirstError
          layout="vertical"
          onFinish={handleSubmit}
          name={`${generatedID}_PolicyClassForm`}
          initialValues={{
            ...selectedItem,
            fullDiscount: selectedItem.insurancePercentage === 100,
            insuranceUnlimited: selectedItem.insuranceUnlimited || false
          }}
        >
          <Row gutter={16}>
            {insuranceCompany && selectedItem?.new ? (
              <Col span={24}>
                <SearchField
                  fieldName="insurancePolicy"
                  rules={[{ required: true, message: t('app.fieldRequired') }]}
                  GET_QUERY={SEARCH_INSURANCE_POLICIES}
                  label={t('settings.insurance.insurancePolicy')}
                  dataName="searchInsurancePolicies"
                  passToQuery={{ insuranceCompany }}
                  setFieldsValue={setFieldsValue}
                  allowClear
                />
              </Col>
            ) : null}
            <Col span={24}>
              <Form.Item label={t('common.name')} name="name" rules={[{ required: true, message: t('app.fieldRequired') }]}>
                <Input autoFocus maxLength={100} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <AmountPriceField
                extra={t('settings.insurance.maxLimitDesc')}
                label={t('settings.insurance.maxLimit')}
                rules={[]}
                name="maxLimit"
                placeholder="0"
              />
            </Col>
            <Col md={12} xs={24}>
              <Form.Item label={t('app.approvalRequired')} name="approvalRequired" valuePropName="checked">
                <Checkbox />
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <Space wrap size="large">
                <AmountPriceField
                  name="insuranceLimit"
                  label={t('calendar.insuranceLimit')}
                  disabled={insuranceUnlimited}
                  step={100}
                  rules={[{ type: 'number', required: !insuranceUnlimited, message: t('calendar.insuranceLimitErr') }]}
                />
                <Form.Item name="insuranceUnlimited" label={t('calendar.unlimited')} valuePropName="checked">
                  <Checkbox />
                </Form.Item>
              </Space>
            </Col>
            <Col md={12} xs={24}>
              <Space wrap size="large">
                <Form.Item
                  label={t('calendar.insuranceDiscount')}
                  name="insurancePercentage"
                  rules={[{ required: !fullDiscount, message: t('app.fieldRequired') }]}
                >
                  <InputNumber
                    disabled={fullDiscount}
                    min={0}
                    max={100}
                    step={10}
                    addonAfter="%"
                    parser={value => toEnglishNumbers(value)}
                  />
                </Form.Item>
                <Form.Item name="fullDiscount" label={'100%'} valuePropName="checked">
                  <Checkbox />
                </Form.Item>
              </Space>
            </Col>
            <Col span={24}>
              <br />
            </Col>
            <Col span={24}>
              <Form.Item name="disabled" valuePropName="checked" label={t('common.disabled')}>
                <Checkbox />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </StyledDrawer>
  )
}

export default PolicyClassForm
